// src/features/links/useMatchNav.ts
// IR-1233 r3 — Ctrl+F-style navigation across search matches.
// IR-1233 r6 — navigate MATCHED ROWS, not individual <mark> elements.
//
// DOM-registry design (deliberate): match positions live in cells three
// components deep (Link anchor / Name / Bio / Source Group chips), so
// threading React state through every cell would bloat props for zero gain —
// while the rows and the `<mark data-search-match>` elements emitted by
// ./highlight are already in the DOM in document order. The hook queries them,
// flips `data-active` imperatively and scrolls the active row into view.
// Imperative attributes are acceptable here because any React re-render that
// discards them also fires the reset effect below (q/items changed → the stale
// index was meaningless anyway).
//
// Why rows: r3 counted marks, which only exist for LOADED rows (50/page). With
// q="CVE" that read "0/53" beside a "117 links" title — a correct count of the
// wrong thing, indistinguishable from 64 lost rows. The backend already knows
// the real answer (`total`), and rows page in from offset 0 in order, so the
// k-th loaded row IS the k-th result globally. Counting rows therefore lets the
// counter show the true "k / 117" from the first keystroke, with no running
// total to explain. Every returned row renders at least one mark (that is what
// the rest of r6 guarantees), so a row and a match are the same thing here.
import { useCallback, useEffect, useRef, useState } from "react";

interface UseMatchNavArgs {
  /** Container whose [data-match-row] elements form the registry. */
  containerRef: React.RefObject<HTMLElement | null>;
  /** Active search query — a change resets the navigation position. */
  q: string | undefined;
  /** Loaded row count — a change (page load / refetch) re-counts the registry. */
  itemCount: number;
  /**
   * Total matching rows reported by the backend (the same number as the "X / Y
   * links" title). Displayed as the denominator so it never disagrees with the
   * title. Falls back to the loaded count when absent.
   */
  totalMatches?: number | undefined;
  /** Whether more pages exist (goNext at the last row pages in more). */
  hasNextPage: boolean;
  fetchNextPage: () => void;
  /**
   * IR-1386 — the current page number, once the table paginates by page rather
   * than by appending. Rows now REPLACE instead of extending the registry, so
   * the "keep the index across a page load" behaviour below would land on the
   * LAST row of the new page and skip the other 49. Passing this switches the
   * hook to paged semantics: crossing the boundary with goNext activates row 1
   * of the incoming page, and changing page by hand clears the active row.
   * Omit it and the original append-based behaviour is preserved verbatim.
   */
  page?: number;
}

interface MatchNavState {
  /** Displayed denominator: every matching row, loaded or not. */
  matchCount: number;
  /** -1 until the first navigation; then the 0-based index of the active row. */
  activeIdx: number;
  goPrev: () => void;
  goNext: () => void;
}

function queryRows(container: HTMLElement | null): HTMLElement[] {
  return Array.from(container?.querySelectorAll<HTMLElement>("[data-match-row]") ?? []);
}

function clearActive(rows: HTMLElement[]) {
  for (const row of rows) {
    row.removeAttribute("data-active");
    for (const mark of row.querySelectorAll("[data-search-match]")) {
      mark.removeAttribute("data-active");
    }
  }
}

/**
 * Activate rows[idx]: exclusive data-active on the row AND on every mark inside
 * it (so the row's own matches take the active highlight), then scroll to it.
 */
function activate(rows: HTMLElement[], idx: number) {
  clearActive(rows);
  const row = rows[idx];
  if (!row) return;
  row.setAttribute("data-active", "true");
  for (const mark of row.querySelectorAll("[data-search-match]")) {
    mark.setAttribute("data-active", "true");
  }
  row.scrollIntoView({ block: "center", behavior: "smooth" });
}

/**
 * Navigate the `[data-match-row]` rows inside `containerRef` in document order.
 * `goNext` at the LAST loaded row calls `fetchNextPage` (keeping the index —
 * the incoming rows extend the list past it) when more pages exist, otherwise
 * wraps to the first row; `goPrev` wraps backward.
 */
export function useMatchNav({
  containerRef,
  q,
  itemCount,
  totalMatches,
  hasNextPage,
  fetchNextPage,
  page,
}: UseMatchNavArgs): MatchNavState {
  const [loadedCount, setLoadedCount] = useState(0);
  const [activeIdx, setActiveIdx] = useState(-1);
  // IR-1386 — set by goNext when IT is what advanced the page, so the effect
  // below can tell "continue the search onto the next page" (activate row 1)
  // apart from "the analyst clicked page 7" (activate nothing).
  const advancedByNav = useRef(false);

  // IR-1386 — paged mode: a page change swaps the whole registry. itemCount is
  // NOT a usable trigger for it (a full page is 50 rows before and after), so
  // key off the page number itself.
  useEffect(() => {
    if (page === undefined) return;
    const rows = queryRows(containerRef.current);
    clearActive(rows);
    setLoadedCount(rows.length);
    if (advancedByNav.current && rows.length > 0) {
      advancedByNav.current = false;
      activate(rows, 0);
      setActiveIdx(0);
    } else {
      setActiveIdx(-1);
    }
  }, [containerRef, page]);

  // A q change invalidates every match: reset position and clear leftover
  // data-active attributes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: q is a deliberate re-run trigger — the rows it invalidates live in the DOM, not in React state.
  useEffect(() => {
    const rows = queryRows(containerRef.current);
    setLoadedCount(rows.length);
    setActiveIdx(-1);
    clearActive(rows);
  }, [containerRef, q]);

  // A loaded-rows change (fetchNextPage / refetch) only re-counts: rows append
  // at the bottom, so the active position stays valid — this is what lets
  // goNext at the page boundary continue INTO the new page instead of
  // restarting at row #1. Clamp in case a refetch shrank the list.
  // biome-ignore lint/correctness/useExhaustiveDependencies: itemCount is a deliberate re-run trigger — the rows it re-counts live in the DOM, not in React state.
  useEffect(() => {
    const rows = queryRows(containerRef.current);
    setLoadedCount(rows.length);
    setActiveIdx((idx) => Math.min(idx, rows.length - 1));
  }, [containerRef, itemCount]);

  const goNext = useCallback(() => {
    const rows = queryRows(containerRef.current);
    if (rows.length === 0) return;
    if (activeIdx >= rows.length - 1) {
      if (hasNextPage) {
        // Boundary: page in more rows. In append mode the index stays (the new
        // rows extend the registry); in paged mode (IR-1386) the rows are
        // REPLACED, so flag it and let the page effect activate row 1.
        if (page !== undefined) advancedByNav.current = true;
        fetchNextPage();
        return;
      }
      activate(rows, 0);
      setActiveIdx(0);
      return;
    }
    const next = activeIdx + 1;
    activate(rows, next);
    setActiveIdx(next);
  }, [containerRef, activeIdx, hasNextPage, fetchNextPage, page]);

  const goPrev = useCallback(() => {
    const rows = queryRows(containerRef.current);
    if (rows.length === 0) return;
    const prev = activeIdx <= 0 ? rows.length - 1 : activeIdx - 1;
    activate(rows, prev);
    setActiveIdx(prev);
  }, [containerRef, activeIdx]);

  return {
    // Show what the backend matched, not what happens to be loaded — the
    // denominator must agree with the "X / Y links" title beside it.
    matchCount: totalMatches ?? loadedCount,
    activeIdx,
    goPrev,
    goNext,
  };
}
